/**
 * LabelView - 标签查看页面（公开访问，用于二维码扫描）
 * 
 * 路由：/view/:labelId
 * 特点：
 * - 无需登录
 * - 只读展示
 * - 移动端优化
 * - 从API获取数据
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { fetchLabelDetail, fetchContentItem } from '../../api/labels';
import { ENV_CONFIG } from '../../config';
import LabelCanvas from './LabelCanvas';
import LabelSkeleton from '../../components/common/LabelSkeleton';
import './styles/LabelView.css';

// lite模式下所有内容都需要懒加载（后端不返回content_data以提升速度）

function LabelView() {
  // 从URL获取labelId
  const labelId = getLabelIdFromURL();
  const isPhonePreview = new URLSearchParams(window.location.search).get('mobile') === '1';

  const [labelData, setLabelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // 用于追踪已加载内容项数量，触发批量更新
  const [loadedCount, setLoadedCount] = useState(0);
  // 用ref存储内容数据，避免频繁setState
  const contentDataRef = useRef({});

  // 记录滚动位置变化
  const lastScrollRef = useRef(0);
  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.scrollY;
      if (Math.abs(currentScroll - lastScrollRef.current) > 50) {
        console.log(`[Scroll] 位置变化: ${lastScrollRef.current} -> ${currentScroll}`);
        if (currentScroll === 0 && lastScrollRef.current > 100) {
          console.warn('[Scroll] ⚠️ 检测到滚动位置被重置到顶部！');
          console.trace('[Scroll] 调用堆栈');
        }
      }
      lastScrollRef.current = currentScroll;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 合并后的labelData（框架 + 已加载的内容）- 使用useMemo缓存
  const mergedLabelData = useMemo(() => {
    if (!labelData) return null;
    console.log(`[LabelView] 合并数据, loadedCount=${loadedCount}`);
    return {
      ...labelData,
      contentItems: labelData.contentItems?.map(item => ({
        ...item,
        contentData: contentDataRef.current[item.itemId] ?? item.contentData
      }))
    };
  }, [labelData, loadedCount]); // loadedCount变化时重新计算

  useEffect(() => {
    const loadLabel = async () => {
      try {
        setLoading(true);
        setError(null);
        contentDataRef.current = {}; // 重置

        // Mock模式下，优先从URL参数解码数据（支持跨设备预览）
        const params = new URLSearchParams(window.location.search);
        const hasEncoded = params.get('mock') === '1' && params.get('d');
        if (ENV_CONFIG?.USE_MOCK && hasEncoded) {
          try {
            const encoded = params.get('d');
            const json = decodeURIComponent(escape(atob(encoded)));
            const parsed = JSON.parse(json);
            setLabelData(parsed);
            return;
          } catch (e) {
            console.warn('解码URL内置标签数据失败，回退到本地存储', e);
          }
        }

        console.log(`[LabelView] 开始加载标签(lite模式): labelId=${labelId}`);
        const data = await fetchLabelDetail(labelId, true);
        console.log('[LabelView] lite模式数据加载成功:', {
          labelName: data.labelName,
          contentItemsCount: data.contentItems?.length
        });
        setLabelData(data);

        // 2. 后台并行加载所有内容项
        if (data.contentItems) {
          const itemsToLoad = data.contentItems.filter(item => item.contentData === null);
          console.log(`[LabelView] 需要懒加载的内容项: ${itemsToLoad.length}个`);

          let loadedItems = 0;
          const totalItems = itemsToLoad.length;

          itemsToLoad.forEach(item => {
            fetchContentItem(labelId, item.itemId).then(fullItem => {
              if (fullItem && fullItem.contentData) {
                // 存入ref，不立即触发渲染
                contentDataRef.current[item.itemId] = fullItem.contentData;
                loadedItems++;

                // 每加载3个或全部加载完成时，批量触发一次渲染
                if (loadedItems % 3 === 0 || loadedItems === totalItems) {
                  setLoadedCount(loadedItems);
                }
              }
            }).catch(err => {
              console.error(`[LabelView] 加载内容项失败: itemId=${item.itemId}`, err);
              loadedItems++;
              if (loadedItems === totalItems) {
                setLoadedCount(loadedItems);
              }
            });
          });
        }
      } catch (err) {
        console.error('加载标签失败:', err);
        setError(err.message || '标签不存在或已被删除');
      } finally {
        setLoading(false);
      }
    };

    if (labelId) {
      loadLabel();
    } else {
      setError('无效的标签ID');
      setLoading(false);
    }
  }, [labelId]);

  // 加载中 - 显示骨架屏
  if (loading) {
    return (
      <div className={`label-view-container ${isPhonePreview ? 'phone-preview' : ''}`}>
        <LabelSkeleton />
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className={`label-view-container ${isPhonePreview ? 'phone-preview' : ''}`}>
        <div className="label-view-error">
          <div className="error-icon">⚠️</div>
          <h2>加载失败</h2>
          <p>{error}</p>
          <button 
            className="btn-retry" 
            onClick={() => window.location.reload()}
          >
            重试
          </button>
        </div>
      </div>
    );
  }

  // 渲染标签内容（使用合并后的数据，包含懒加载的内容）
  return <LabelCanvas data={mergedLabelData} phonePreview={isPhonePreview} />;
}

/**
 * 从URL获取labelId
 * 支持：/view/abc123 或 ?id=abc123
 */
function getLabelIdFromURL() {
  const path = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);
  
  // 从路径获取: /view/abc123
  const pathMatch = path.match(/\/view\/([^/]+)/);
  if (pathMatch) {
    return pathMatch[1];
  }
  
  // 从查询参数获取: ?id=abc123
  return searchParams.get('id') || searchParams.get('labelId');
}

// ✅ 所有渲染逻辑已移至共享组件 label-content/
// 现在预览和实际查看使用完全相同的渲染逻辑，确保所见即所得！

export default LabelView;
